import React, { useState, useEffect, useRef } from 'react';
import DemoRequestForm from './DemoRequestForm';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';

export interface ILandingPage {}

/* ───────────────────── Data ───────────────────── */

const navItems = [
    { id: 'hero', label: 'Anasayfa' },
    { id: 'about', label: 'Hakkımızda' },
    { id: 'features', label: 'Özellikler' },
    { id: 'how', label: 'Nasıl Çalışır' },
    { id: 'partners', label: 'İş Ortaklarımız' },
    { id: 'pricing', label: 'Fiyatlandırma' },
    { id: 'contact', label: 'İletişim' },
];

const contactChannels = [
    { icon: '📞', label: 'Telefon', value: '0533 260 44 99', href: 'tel:+905332604499' },
    { icon: '✉️', label: 'E-Posta', value: 'evren.demir@co-hr.com.tr', href: 'mailto:evren.demir@co-hr.com.tr' },
    { icon: '🌐', label: 'Web', value: 'www.co-hr.com.tr', href: 'https://www.co-hr.com.tr' },
    { icon: '📍', label: 'Adres', value: 'DÜZCE Üniversitesi Teknopark', href: 'https://maps.google.com/?q=D%C3%BCzce+%C3%9Cniversitesi+Teknopark' },
];

const features = [
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
            </svg>
        ),
        title: 'OKR & Hedef Yönetimi',
        desc: 'Şirket, ekip ve bireysel hedeflerinizi tanımlayın. Gerçek zamanlı ilerleme takibi ile herkes nerede olduğunu bilsin.',
        color: '#6366f1',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8">
                <path d="M3 3v18h18" />
                <path d="M7 16l4-6 4 4 5-8" />
            </svg>
        ),
        title: 'Anlık Performans Analitik',
        desc: 'Dinamik dashboard\'lar ve görsel raporlarla performans verilerinizi anlık analiz edin, stratejik kararlar alın.',
        color: '#0ea5e9',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
            </svg>
        ),
        title: '360° Geri Bildirim',
        desc: 'Yöneticiden çalışana, çalışandan yöneticiye — çok yönlü değerlendirmelerle kapsamlı geri bildirim kültürü oluşturun.',
        color: '#8b5cf6',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18M9 21V9" />
            </svg>
        ),
        title: 'Performans Kartları',
        desc: 'Her çalışan için özelleştirilebilir performans kartları oluşturun. Yetkinlik, hedef ve KPI\'ları tek bir yerde yönetin.',
        color: '#f59e0b',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
        ),
        title: 'Ölçek Bazlı Değerlendirme',
        desc: 'Farklı değerlendirme ölçekleri tanımlayın. Likert, yüzdelik veya özel ölçeklerle değerlendirme yapın.',
        color: '#10b981',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
            </svg>
        ),
        title: 'Detaylı Raporlama & Excel',
        desc: 'Departman bazlı, bireysel ve şirket geneli raporlar oluşturun. Tek tıkla Excel\'e aktarın.',
        color: '#ef4444',
    },
];

const steps = [
    { num: '01', title: 'Hedefleri Belirleyin', desc: 'Şirket stratejinizi OKR\'lara dönüştürün, ekip ve bireysel hedefleri kaskadlayın.' },
    { num: '02', title: 'Performansı İzleyin', desc: 'Gerçek zamanlı panellerle ilerlemeyi takip edin, darboğazları erken tespit edin.' },
    { num: '03', title: 'Geri Bildirim Toplayın', desc: '360° değerlendirmelerle çok yönlü geri bildirim kültürü oluşturun.' },
    { num: '04', title: 'Analiz Edin & Geliştirin', desc: 'Detaylı raporlar ve analizlerle sürekli iyileştirme döngüsü kurun.' },
];

const partners = [
    { name: 'Arentech Bilişim', url: 'https://www.arentech.com.tr' },
    { name: 'Açık Holding', url: 'https://www.acik.com' },
    { name: 'Mikrolink Bilişim', url: 'https://www.mikrolink.com.tr' },
    { name: 'Farmatek', url: 'https://www.farmatek.com.tr' },
    { name: 'Bupa Acıbadem', url: 'https://www.bupaacibadem.com.tr' },
    { name: 'Yüce Oto', url: 'https://www.skoda.com.tr' },
    { name: 'DoubleTree by Hilton', url: 'https://www.hilton.com.tr' },
    { name: 'C.E.S. Savunma', url: 'http://www.ces.com.tr' },
];

const pricing = [
    {
        tier: 'Startup',
        price: '₺4.900',
        period: '/ay',
        desc: 'Küçük ekipler için',
        features: ['25 kullanıcıya kadar', 'Temel OKR yönetimi', 'Performans kartları', 'E-posta desteği'],
        highlighted: false,
    },
    {
        tier: 'Business',
        price: '₺12.900',
        period: '/ay',
        desc: 'Büyüyen şirketler için',
        features: ['150 kullanıcıya kadar', 'Gelişmiş analitik', '360° geri bildirim', 'Excel raporlama', 'Öncelikli destek'],
        highlighted: true,
    },
    {
        tier: 'Enterprise',
        price: 'Özel',
        period: '',
        desc: 'Kurumsal çözümler',
        features: ['Sınırsız kullanıcı', 'Özel entegrasyon', 'On-Premise kurulum', 'Müşteri Başarı Yöneticisi', 'SLA garantisi'],
        highlighted: false,
    },
];

const stats = [
    { value: '10K+', label: 'Aktif Kullanıcı' },
    { value: '50+', label: 'Kurumsal Müşteri' },
    { value: '%98', label: 'Müşteri Memnuniyeti' },
    { value: '2M+', label: 'Değerlendirme' },
];

/* ───────────────────── Animated Logo ───────────────────── */

const AnimatedLogo: React.FC<{ size?: number; className?: string }> = ({ size = 40, className = '' }) => {
    const s = size;
    const viewBox = 64;
    return (
        <motion.div className={`flex items-center gap-2.5 ${className}`}>
            <motion.svg
                width={s}
                height={s}
                viewBox={`0 0 ${viewBox} ${viewBox}`}
                initial="hidden"
                animate="visible"
            >
                <defs>
                    <linearGradient id="logoGradMain" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#4f46e5" />
                        <stop offset="100%" stopColor="#0ea5e9" />
                    </linearGradient>
                    <linearGradient id="logoGradAccent" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#0ea5e9" />
                        <stop offset="100%" stopColor="#10b981" />
                    </linearGradient>
                </defs>

                {/* Hexagon frame — draws on like a pen stroke */}
                <motion.path
                    d="M32 4 L56 18 L56 46 L32 60 L8 46 L8 18 Z"
                    fill="none"
                    stroke="url(#logoGradMain)"
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                    variants={{
                        hidden: { pathLength: 0, opacity: 0 },
                        visible: { pathLength: 1, opacity: 1, transition: { duration: 1, ease: 'easeInOut' } },
                    }}
                />

                {/* Person head */}
                <motion.circle
                    cx="32" cy="22" r="6"
                    fill="url(#logoGradMain)"
                    variants={{
                        hidden: { scale: 0, opacity: 0 },
                        visible: { scale: 1, opacity: 1, transition: { delay: 0.7, duration: 0.4, ease: 'backOut' } },
                    }}
                />

                {/* Person shoulders — rounded arc */}
                <motion.path
                    d="M20 44 C20 36 26 31 32 31 C38 31 44 36 44 44"
                    fill="none"
                    stroke="url(#logoGradMain)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    variants={{
                        hidden: { pathLength: 0, opacity: 0 },
                        visible: { pathLength: 1, opacity: 1, transition: { delay: 0.9, duration: 0.5, ease: 'easeOut' } },
                    }}
                />

                {/* Rising chart bar left */}
                <motion.rect
                    x="14" y="36" width="4" height="12" rx="2"
                    fill="url(#logoGradAccent)"
                    variants={{
                        hidden: { scaleY: 0, opacity: 0 },
                        visible: { scaleY: 1, opacity: 0.85, transition: { delay: 1.2, duration: 0.35, ease: 'backOut' } },
                    }}
                    style={{ originY: '100%', transformBox: 'fill-box' }}
                />

                {/* Rising chart bar right (taller) */}
                <motion.rect
                    x="46" y="30" width="4" height="18" rx="2"
                    fill="url(#logoGradAccent)"
                    variants={{
                        hidden: { scaleY: 0, opacity: 0 },
                        visible: { scaleY: 1, opacity: 0.85, transition: { delay: 1.35, duration: 0.35, ease: 'backOut' } },
                    }}
                    style={{ originY: '100%', transformBox: 'fill-box' }}
                />

                {/* Upward arrow — symbolises growth */}
                <motion.path
                    d="M46 18 L50 12 L54 18"
                    fill="none"
                    stroke="url(#logoGradAccent)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    variants={{
                        hidden: { pathLength: 0, opacity: 0 },
                        visible: { pathLength: 1, opacity: 0.9, transition: { delay: 1.5, duration: 0.3 } },
                    }}
                />
            </motion.svg>

            {/* Wordmark */}
            <div className="flex items-baseline gap-0">
                <motion.span
                    className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-indigo-600 to-sky-500 bg-clip-text text-transparent"
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                >
                    CO
                </motion.span>
                <motion.span
                    className="text-2xl font-extrabold tracking-tight text-gray-300 mx-px"
                    initial={{ opacity: 0, scaleY: 0 }}
                    animate={{ opacity: 1, scaleY: 1 }}
                    transition={{ delay: 0.5, duration: 0.3 }}
                >
                    -
                </motion.span>
                <motion.span
                    className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-sky-500 to-emerald-500 bg-clip-text text-transparent"
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6, duration: 0.5 }}
                >
                    HR
                </motion.span>
            </div>
        </motion.div>
    );
};

/* ───────────────────── Floating Particles BG ───────────────────── */

const FloatingParticles: React.FC = () => {
    const particles = Array.from({ length: 30 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 4 + 2,
        duration: Math.random() * 20 + 15,
        delay: Math.random() * 5,
    }));

    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {particles.map((p) => (
                <motion.div
                    key={p.id}
                    className="absolute rounded-full bg-indigo-500/10"
                    style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size }}
                    animate={{
                        y: [0, -30, 0],
                        x: [0, 15, 0],
                        opacity: [0.15, 0.4, 0.15],
                    }}
                    transition={{
                        duration: p.duration,
                        delay: p.delay,
                        repeat: Infinity,
                        ease: 'easeInOut',
                    }}
                />
            ))}
        </div>
    );
};

/* ───────────────────── Animated Counter ───────────────────── */

const Counter: React.FC<{ value: string; label: string }> = ({ value, label }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });
    const numMatch = value.match(/(\d+)/);
    const num = numMatch ? parseInt(numMatch[1]) : 0;
    const prefix = value.match(/^(%|₺)?/)?.[0] || '';
    const suffix = value.replace(/^(%|₺)?\d+/, '');
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!isInView) return;
        let start = 0;
        const end = num;
        const duration = 2000;
        const stepTime = Math.max(Math.floor(duration / end), 16);
        const timer = setInterval(() => {
            start += Math.ceil(end / (duration / stepTime));
            if (start >= end) {
                setCount(end);
                clearInterval(timer);
            } else {
                setCount(start);
            }
        }, stepTime);
        return () => clearInterval(timer);
    }, [isInView, num]);

    return (
        <div ref={ref} className="text-center">
            <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-sky-600 bg-clip-text text-transparent">
                {prefix}{isInView ? count.toLocaleString('tr-TR') : '0'}{suffix}
            </div>
            <div className="text-gray-500 mt-2 text-sm md:text-base">{label}</div>
        </div>
    );
};

/* ───────────────────── Section Reveal ───────────────────── */

const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({
    children,
    delay = 0,
    className = '',
}) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-80px' });

    return (
        <motion.div
            ref={ref}
            className={className}
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay, ease: 'easeOut' }}
        >
            {children}
        </motion.div>
    );
};

/* ───────────────────── Main Component ───────────────────── */

const LandingPage: React.FC<ILandingPage> = () => {
    useDocumentTitle('CO-HR | Performans Yönetim Sistemi');
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { scrollYProgress } = useScroll();
    const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
    const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 0.95]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const scrollTo = (id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        setMenuOpen(false);
    };

    return (
        <div className="font-sans bg-white text-gray-900 scroll-smooth">
            {/* ─── Progress Bar ─── */}
            <motion.div
                className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-500 z-[60] origin-left"
                style={{ scaleX: scrollYProgress }}
            />

            {/* ─── Header ─── */}
            <header
                className={`fixed top-0 w-full z-50 transition-all duration-300 ${
                    scrolled ? 'bg-white/90 backdrop-blur-xl shadow-lg shadow-gray-200/50' : 'bg-transparent'
                }`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16 md:h-20">
                    <button onClick={() => scrollTo('hero')} className="focus:outline-none">
                        <AnimatedLogo size={36} />
                    </button>

                    {/* Desktop nav */}
                    <nav className="hidden md:flex items-center gap-8">
                        {navItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => scrollTo(item.id)}
                                className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium focus:outline-none"
                            >
                                {item.label}
                            </button>
                        ))}
                        <button
                            onClick={() => scrollTo('contact')}
                            className="ml-2 px-5 py-2 rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 text-white text-sm font-semibold hover:shadow-lg hover:shadow-indigo-500/25 transition-all focus:outline-none"
                        >
                            Demo Talep Et
                        </button>
                    </nav>

                    {/* Mobile burger */}
                    <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 focus:outline-none">
                        <svg className="h-6 w-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d={menuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
                            />
                        </svg>
                    </button>
                </div>

                {/* Mobile menu */}
                <AnimatePresence>
                    {menuOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="md:hidden bg-white/95 backdrop-blur-xl overflow-hidden border-t border-gray-100"
                        >
                            <div className="px-4 py-4 space-y-3">
                                {navItems.map((item) => (
                                    <button
                                        key={item.id}
                                        onClick={() => scrollTo(item.id)}
                                        className="block w-full text-left text-gray-600 hover:text-gray-900 py-2 text-base focus:outline-none"
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </header>

            <main>
                {/* ─── Hero ─── */}
                <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
                    {/* Gradient background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-indigo-50/50 to-sky-50/30" />

                    {/* Animated gradient orbs */}
                    <motion.div
                        className="absolute -top-20 -left-40 w-[500px] h-[500px] bg-indigo-400/25 rounded-full blur-[100px]"
                        animate={{ x: [0, 120, 60, 0], y: [0, 60, -40, 0], scale: [1, 1.15, 0.95, 1] }}
                        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <motion.div
                        className="absolute top-1/3 -right-20 w-[450px] h-[450px] bg-sky-400/25 rounded-full blur-[100px]"
                        animate={{ x: [0, -80, -30, 0], y: [0, -60, 80, 0], scale: [1, 0.9, 1.1, 1] }}
                        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <motion.div
                        className="absolute bottom-10 left-1/4 w-[400px] h-[400px] bg-emerald-300/20 rounded-full blur-[100px]"
                        animate={{ x: [0, 70, -40, 0], y: [0, -50, 30, 0], scale: [1, 1.2, 0.9, 1] }}
                        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <motion.div
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-300/15 rounded-full blur-[120px]"
                        animate={{ scale: [1, 1.3, 0.95, 1], rotate: [0, 45, -20, 0] }}
                        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <motion.div
                        className="absolute bottom-1/4 right-1/4 w-[350px] h-[350px] bg-pink-300/15 rounded-full blur-[100px]"
                        animate={{ x: [0, -60, 40, 0], y: [0, 40, -60, 0] }}
                        transition={{ duration: 13, repeat: Infinity, ease: 'easeInOut' }}
                    />

                    <FloatingParticles />

                    <motion.div
                        className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center"
                        style={{ opacity: heroOpacity, scale: heroScale }}
                    >
                        {/* Title */}
                        <motion.h1
                            className="text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-tight mb-6"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.8 }}
                        >
                            <span className="text-gray-900">Performansı </span>
                            <span className="bg-gradient-to-r from-indigo-600 via-sky-500 to-emerald-500 bg-clip-text text-transparent">
                                Ölçün, Yönetin
                            </span>
                            <br />
                            <span className="text-gray-900">ve </span>
                            <span className="bg-gradient-to-r from-sky-500 to-emerald-500 bg-clip-text text-transparent">
                                Geliştirin
                            </span>
                        </motion.h1>

                        <motion.p
                            className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto mb-10"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6, duration: 0.6 }}
                        >
                            CO-HR ile OKR ve KPI hedeflerinizi belirleyin, performans kartlarıyla çalışanlarınızı değerlendirin
                            ve 360° geri bildirimlerle sürekli gelişim kültürü oluşturun.
                        </motion.p>

                        <motion.div
                            className="flex flex-col sm:flex-row gap-4 justify-center"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8, duration: 0.6 }}
                        >
                            <button
                                onClick={() => scrollTo('contact')}
                                className="px-8 py-4 rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 text-white font-semibold text-lg hover:shadow-xl hover:shadow-indigo-500/25 transition-all hover:scale-105"
                            >
                                Ücretsiz Demo
                            </button>
                            <button
                                onClick={() => scrollTo('features')}
                                className="px-8 py-4 rounded-full border border-gray-300 text-gray-700 font-semibold text-lg hover:bg-gray-50 transition-all"
                            >
                                Keşfedin
                            </button>
                        </motion.div>

                        {/* Stats row */}
                        <motion.div
                            className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.2, duration: 0.8 }}
                        >
                            {stats.map((s, i) => (
                                <Counter key={i} value={s.value} label={s.label} />
                            ))}
                        </motion.div>
                    </motion.div>

                    {/* Scroll indicator */}
                    <motion.div
                        className="absolute bottom-8 left-1/2 -translate-x-1/2"
                        animate={{ y: [0, 10, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                    </motion.div>
                </section>

                {/* ─── Hakkımızda ─── */}
                <section id="about" className="py-24 md:py-32 relative overflow-hidden">
                    <div className="absolute inset-0 bg-white" />
                    <motion.div
                        className="absolute top-1/4 left-0 w-[520px] h-[520px] bg-sky-200/20 rounded-full blur-3xl"
                        animate={{ x: [0, 40, 0], y: [0, -30, 0] }}
                        transition={{ duration: 20, repeat: Infinity }}
                    />
                    <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                        <Reveal>
                            <div className="text-center mb-14">
                                <span className="text-sm font-semibold tracking-wider uppercase text-indigo-600">Co-HR</span>
                                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 text-gray-900">
                                    Hakkımızda
                                </h2>
                            </div>
                        </Reveal>

                        <Reveal delay={0.1}>
                            <p className="text-lg text-gray-600 leading-relaxed text-center max-w-4xl mx-auto">
                                Co-HR olarak, modern iş dünyasının en dinamik ve kritik unsurlarından biri olan
                                İnsan Kaynakları süreçlerini stratejik bir bakış açısıyla yeniden şekillendiriyoruz.
                                Kurumların en değerli varlığı olan insan kaynağını en doğru modellerle
                                yönetebilmeleri adına; İş Değerleme süreçlerini yürütüyor, Kademe ve Unvan
                                yapıları kuruyor, etkin ve adil Ücret ve Yan Haklar yaklaşımları geliştiriyor ve
                                sürdürülebilir Performans Sistemleri tasarlıyoruz.
                            </p>
                        </Reveal>

                        <Reveal delay={0.2}>
                            <div className="mt-14 p-8 sm:p-10 rounded-3xl bg-gradient-to-br from-slate-50 to-indigo-50/40 border border-gray-100">
                                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-5">
                                    PYS{' '}
                                    <span className="bg-gradient-to-r from-indigo-600 to-sky-500 bg-clip-text text-transparent">
                                        (Performans Yönetim Sistemi)
                                    </span>{' '}
                                    Nedir?
                                </h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Co-HR'ın sürdürülebilir ve ölçülebilir performans yönetimi uzmanlığını dijital
                                    ortama taşıdığımız Performans Yönetim Sistemi (PYS); şirketlerin kurumsal
                                    hedefleri ile çalışan yetkinliklerini tek bir platformda buluşturan yenilikçi
                                    bir yazılımdır. Hem KPI (Temel Performans Göstergeleri) tabanlı geleneksel
                                    ölçüm modellerini hem de OKR (Hedefler ve Sonuçlar) odaklı yeni nesil çevik
                                    yönetim yaklaşımlarını esnek bir şekilde destekleyen hibrit bir mimariye
                                    sahiptir. Şirketinizin dinamiklerine göre ister KPI, ister OKR, isterseniz her
                                    iki modeli entegre bir şekilde uygulamanıza olanak tanır.
                                </p>
                                <p className="text-gray-600 leading-relaxed mt-5">
                                    Kurumların stratejik İK kararlarını veri odaklı analizlerle desteklerken;
                                    geleneksel, hantal ve kağıt üzerindeki değerlendirme süreçlerini geride
                                    bırakıyoruz. Co-HR'ın hibrit PYS modülü ile çalışanlarınızın potansiyelini
                                    doğru hamlelerle açığa çıkarıyor, gelişim alanlarını şeffafça takip ediyor ve
                                    sürdürülebilir yüksek performansı şirket kültürünüzün bir parçası haline
                                    getiriyoruz.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </section>

                {/* ─── Features ─── */}
                <section id="features" className="py-24 md:py-32 relative">
                    <div className="absolute inset-0 bg-white" />
                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <Reveal>
                            <div className="text-center mb-16">
                                <span className="text-sm font-semibold tracking-wider uppercase text-indigo-600">Özellikler</span>
                                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 text-gray-900">
                                    Performans yönetiminin{' '}
                                    <span className="bg-gradient-to-r from-indigo-600 to-sky-500 bg-clip-text text-transparent">
                                        tüm ihtiyaçları
                                    </span>
                                </h2>
                                <p className="text-gray-500 mt-4 max-w-2xl mx-auto text-lg">
                                    Tek platformda hedef belirleme, değerlendirme, geri bildirim ve raporlama.
                                </p>
                            </div>
                        </Reveal>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {features.map((f, i) => (
                                <Reveal key={i} delay={i * 0.1}>
                                    <motion.div
                                        className="group p-6 rounded-2xl bg-gray-50/80 border border-gray-100 hover:border-gray-200 transition-all duration-300 hover:shadow-lg hover:shadow-gray-100"
                                        whileHover={{ y: -4 }}
                                    >
                                        <div
                                            className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                                            style={{ backgroundColor: f.color + '15', color: f.color }}
                                        >
                                            {f.icon}
                                        </div>
                                        <h3 className="text-xl font-semibold mb-2 text-gray-900">{f.title}</h3>
                                        <p className="text-gray-500 leading-relaxed">{f.desc}</p>
                                    </motion.div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ─── How it Works ─── */}
                <section id="how" className="py-24 md:py-32 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white" />
                    <motion.div
                        className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-200/20 rounded-full blur-3xl"
                        animate={{ x: [0, -30, 0], y: [0, 40, 0] }}
                        transition={{ duration: 18, repeat: Infinity }}
                    />
                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <Reveal>
                            <div className="text-center mb-16">
                                <span className="text-sm font-semibold tracking-wider uppercase text-emerald-600">Süreç</span>
                                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 text-gray-900">
                                    Nasıl{' '}
                                    <span className="bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-transparent">
                                        Çalışır?
                                    </span>
                                </h2>
                            </div>
                        </Reveal>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {steps.map((s, i) => (
                                <Reveal key={i} delay={i * 0.15}>
                                    <div className="text-center">
                                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-sky-400 text-white text-2xl font-bold mb-5">
                                            {s.num}
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{s.title}</h3>
                                        <p className="text-gray-500 leading-relaxed">{s.desc}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ─── Partners ─── */}
                <section id="partners" className="py-24 md:py-32 relative">
                    <div className="absolute inset-0 bg-white" />
                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <Reveal>
                            <div className="text-center mb-16">
                                <span className="text-sm font-semibold tracking-wider uppercase text-sky-600">İş Ortakları</span>
                                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 text-gray-900">
                                    Birlikte{' '}
                                    <span className="bg-gradient-to-r from-sky-500 to-indigo-500 bg-clip-text text-transparent">
                                        büyüdüğümüz şirketler
                                    </span>
                                </h2>
                            </div>
                        </Reveal>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {partners.map((p, i) => (
                                <Reveal key={i} delay={i * 0.05}>
                                    <a
                                        href={p.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block"
                                    >
                                        <motion.div
                                            className="p-6 rounded-2xl bg-gray-50 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all text-center"
                                            whileHover={{ scale: 1.03 }}
                                        >
                                            <div className="text-lg font-semibold text-gray-700">{p.name}</div>
                                        </motion.div>
                                    </a>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ─── Pricing ─── */}
                <section id="pricing" className="py-24 md:py-32 relative">
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-indigo-50/30 to-slate-50" />
                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <Reveal>
                            <div className="text-center mb-16">
                                <span className="text-sm font-semibold tracking-wider uppercase text-amber-600">Fiyatlandırma</span>
                                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 text-gray-900">
                                    Her ölçeğe uygun{' '}
                                    <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                                        planlar
                                    </span>
                                </h2>
                            </div>
                        </Reveal>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                            {pricing.map((plan, i) => (
                                <Reveal key={i} delay={i * 0.1}>
                                    <motion.div
                                        className={`relative p-8 rounded-2xl border transition-all duration-300 ${
                                            plan.highlighted
                                                ? 'bg-gradient-to-b from-indigo-50 to-sky-50 border-indigo-200 shadow-lg shadow-indigo-100/50'
                                                : 'bg-white border-gray-100 hover:border-gray-200 hover:shadow-lg hover:shadow-gray-100'
                                        }`}
                                        whileHover={{ y: -4 }}
                                    >
                                        {plan.highlighted && (
                                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 text-white text-xs font-semibold">
                                                Popüler
                                            </div>
                                        )}
                                        <h3 className="text-xl font-semibold text-gray-900 mb-1">{plan.tier}</h3>
                                        <p className="text-gray-500 text-sm mb-4">{plan.desc}</p>
                                        <div className="mb-6">
                                            <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                                            <span className="text-gray-500">{plan.period}</span>
                                        </div>
                                        <ul className="space-y-3 mb-8">
                                            {plan.features.map((feat, j) => (
                                                <li key={j} className="flex items-start gap-3 text-gray-600 text-sm">
                                                    <svg className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    {feat}
                                                </li>
                                            ))}
                                        </ul>
                                        <button
                                            onClick={() => scrollTo('contact')}
                                            className={`w-full py-3 rounded-full font-semibold text-sm transition-all ${
                                                plan.highlighted
                                                    ? 'bg-gradient-to-r from-indigo-500 to-sky-500 text-white hover:shadow-lg hover:shadow-indigo-500/25'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                        >
                                            {plan.price === 'Özel' ? 'İletişime Geçin' : 'Başlayın'}
                                        </button>
                                    </motion.div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ─── Contact / CTA ─── */}
                <section id="contact" className="py-24 md:py-32 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-white to-slate-50" />
                    <motion.div
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-indigo-200/30 rounded-full blur-3xl"
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 8, repeat: Infinity }}
                    />
                    <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                        <Reveal>
                            <div className="text-center mb-14">
                                <span className="text-sm font-semibold tracking-wider uppercase text-indigo-600">İletişim</span>
                                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 text-gray-900">
                                    İletişim{' '}
                                    <span className="bg-gradient-to-r from-indigo-600 via-sky-500 to-emerald-500 bg-clip-text text-transparent">
                                        Kanallarımız
                                    </span>
                                </h2>
                                <p className="text-gray-600 mt-5 text-lg leading-relaxed max-w-3xl mx-auto">
                                    İnsan kaynakları süreçlerinizde dijital dönüşümü başlatmak, hibrit mimariye
                                    sahip Performans Yönetim Sistemi (PYS) çözümlerimiz hakkında detaylı bilgi
                                    almak veya merak ettiğiniz sorulara yanıt bulmak için bizimle dilediğiniz
                                    zaman iletişime geçebilirsiniz.
                                </p>
                                <p className="text-gray-900 mt-4 text-lg font-medium">
                                    Siz yolunuzda, Co-HR yanınızda… Sizlere en iyi şekilde destek olmak için
                                    bizler buradayız.
                                </p>
                            </div>
                        </Reveal>

                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
                            {/* İletişim bilgileri */}
                            <Reveal delay={0.1} className="lg:col-span-2">
                                <div className="space-y-4">
                                    {contactChannels.map((c) => (
                                        <a
                                            key={c.label}
                                            href={c.href}
                                            target={c.href.startsWith('http') ? '_blank' : undefined}
                                            rel={c.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                            className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-indigo-200 transition"
                                        >
                                            <span className="text-2xl leading-none">{c.icon}</span>
                                            <span className="min-w-0">
                                                <span className="block text-sm text-gray-500">{c.label}</span>
                                                <span className="block text-gray-900 font-medium break-words">{c.value}</span>
                                            </span>
                                        </a>
                                    ))}
                                </div>
                            </Reveal>

                            {/* Demo talep formu */}
                            <Reveal delay={0.2} className="lg:col-span-3">
                                <DemoRequestForm />
                            </Reveal>
                        </div>
                    </div>
                </section>

                {/* ─── Footer ─── */}
                <footer className="border-t border-gray-100 bg-gray-50 py-12">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-12">
                            <div className="md:max-w-xs">
                                <AnimatedLogo size={32} />
                                <p className="text-gray-500 mt-4 text-sm leading-relaxed">
                                    Yeni nesil performans yönetim platformu. Hedeflerinizi belirleyin, ölçün ve geliştirin.
                                </p>
                            </div>
                            <div className="flex gap-16">
                                <div>
                                    <h5 className="font-semibold text-gray-900 mb-4">Platform</h5>
                                    <ul className="space-y-2 text-sm text-gray-500">
                                        <li><button onClick={() => scrollTo('features')} className="hover:text-gray-900 transition focus:outline-none">Özellikler</button></li>
                                        <li><button onClick={() => scrollTo('pricing')} className="hover:text-gray-900 transition focus:outline-none">Fiyatlandırma</button></li>
                                        <li><button className="hover:text-gray-900 transition focus:outline-none">Entegrasyonlar</button></li>
                                    </ul>
                                </div>
                                <div>
                                    <h5 className="font-semibold text-gray-900 mb-4">Şirket</h5>
                                    <ul className="space-y-2 text-sm text-gray-500">
                                        <li><a href="https://co-hr.com.tr/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900 transition">Hakkımızda</a></li>
                                        <li><a href="https://co-hr.com.tr/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900 transition">Kariyer</a></li>
                                        <li><a href="https://co-hr.com.tr/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900 transition">İletişim</a></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
                            <p className="text-gray-400 text-sm">Bu bir <a href="https://co-hr.com.tr/" target="_blank" rel="noopener noreferrer" className="font-semibold text-gray-600 hover:text-indigo-500 transition">CO-HR</a> ürünüdür.</p>
                            <div className="flex gap-6 text-sm text-gray-400">
                                <Link to="/gizlilik" className="hover:text-gray-700 transition">Gizlilik</Link>
                                <Link to="/kullanim-kosullari" className="hover:text-gray-700 transition">Kullanım Koşulları</Link>
                                <Link to="/kvkk" className="hover:text-gray-700 transition">KVKK</Link>
                            </div>
                        </div>
                    </div>
                </footer>
            </main>
        </div>
    );
};

export default LandingPage;
